import { IPageLink, IUser } from '@vivli/shared/infrastructure/interface';
import { AssignedAppTypeEnum, PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import i18next from 'i18next';
import { useUserPermissions } from '@vivli/features/users/infrastructure/hook';
import { AssetsConstant } from '@vivli/shared/infrastructure/constants';
import {
    useActiveUserContext,
    useAssignedAppTypeContext,
    useOrganizationsApiContext,
    useAuthServiceContext
} from '@vivli/core/infrastructure/context';
import { useEffect, useState } from 'react';
import { IOrganization } from '@vivli/features/organizations/infrastructure/interface';
import { useTranslation } from 'react-i18next';

//note for amr - this only provides tabs/routes for the LEFT TABS
//for the dropdown menu entries/routes, see use-user-menu-routes.hook.ts
export function useUserRouteMapperAmrHook() {
    const { isDatasetOwner, isOrgOrVivliAdmin, isDataProvider, isAnnotator, isQaReviewer, isIRPApprover, isPresumedResearcher } =
        useUserPermissions();
    const { userHasPermission } = useActiveUserContext();
    const { t } = useTranslation();
    const { isAmr } = useAssignedAppTypeContext();
    const [organizations, setOrganizations] = useState<IOrganization[]>();
    const organizationsService = useOrganizationsApiContext();

    const order = [
        'Dashboard',
        'Data Requests',
        'Enquiries',
        'Studies',
        'Reports',
        'Research Environments',
        'My Organizations',
        'My Organization',
        'Users',
        'Study Submissions',
        'Admin Console'
    ];
    const getUserRoutes = (
        user: IUser //user here is logged in user
    ): Array<{
        name?: string;
        title?: string;
        icon?: { active: string; inactive: string };
        url?: string;
        defaultTab?: string;
        subLinks?: IPageLink[];
        managementTab?: boolean; //tab used for "management" only, meaning won't appear on top menu
        //appears on right-hand menu instead
    }> => {
        if (!user) {
            return [];
        }
        //TODO PERMFE - redo with permissions+
        const orgs = user?.orgMemberships?.filter((om) => om.vivliPermissions?.includes(PermissionsEnum.ProviderActAdv));

        let orgId = null;
        if (orgs && orgs.length !== 0) {
            [{ orgId }] = orgs;
        }

        const links: Array<{
            name?: string;
            title?: string;
            icon?: { active: string; inactive: string };
            url?: string;
            defaultTab?: string;
            managementTab?: boolean; //tab used for "management" only, meaning won't appear on top menu
            //appears on right-hand menu instead
        }> = [
            {
                title: 'Dashboard',
                icon: {
                    active: AssetsConstant.DASHBOARD_ICON_ACTIVE,
                    inactive: AssetsConstant.DASHBOARD_ICON_INACTIVE
                },
                url: 'dashboard',
                defaultTab: ''
            },
            {} // empty placeholder key for admin portal side nav panels
        ];

        //TODO PERMFE - redo with permissions+
        const isVivliAdmin = userHasPermission(user, PermissionsEnum.DrActAdv);
        const isOpsAdmin = userHasPermission(user, PermissionsEnum.UseAdmConsoleAdv);

        if (user.orgMemberships) {
            if (isDataProvider || isIRPApprover) {
                links.splice(1, 0, {
                    title: 'Data Requests',
                    icon: {
                        active: AssetsConstant.REQUESTS_ICON_ACTIVE,
                        inactive: AssetsConstant.REQUESTS_ICON_INACTIVE
                    },
                    url: 'requests',
                    defaultTab: isPresumedResearcher ? 'draft' : 'AwaitingMyAction'
                });
            }
            if (isAnnotator || isQaReviewer || isDataProvider || isOrgOrVivliAdmin) {
                links.splice(1, 0, {
                    title: t('studies'),
                    icon: {
                        active: AssetsConstant.TRIALS_ICON_ACTIVE,
                        inactive: AssetsConstant.TRIALS_ICON_INACTIVE
                    },
                    url: 'studies',
                    defaultTab: 'posted'
                });
            }

            //TODO PERMFE - redo with permissions+
            if (isOrgOrVivliAdmin) {
                links.splice(1, 0, {
                    title: 'My Organizations',
                    icon: {
                        active: AssetsConstant.OFFICEBUILDING_ICON_ACTIVE,
                        inactive: AssetsConstant.OFFICEBUILDING_ICON_INACTIVE
                    },
                    url: !isVivliAdmin && !(orgs?.length > 1) ? `orgs/${orgId}` : 'orgs',
                    defaultTab: '',
                    managementTab: true
                });

                links.splice(1, 0, {
                    title: 'Reports',
                    icon: {
                        active: AssetsConstant.TRIALS_ICON_ACTIVE,
                        inactive: AssetsConstant.TRIALS_ICON_INACTIVE
                    },
                    url: `report`,
                    defaultTab: ''
                });

                links.splice(1, 0, {
                    title: 'Users',
                    icon: {
                        active: AssetsConstant.ORGANIZATIONS_ICON_ACTIVE,
                        inactive: AssetsConstant.ORGANIZATIONS_ICON_INACTIVE
                    },
                    url: `users`,
                    defaultTab: '',
                    managementTab: true
                });
            }
        } else {
            //TODO PERMFE - redo with permissions+
            if (isVivliAdmin || isOpsAdmin) {
                links.splice(1, 0, {
                    title: 'My Organizations',
                    icon: {
                        active: AssetsConstant.OFFICEBUILDING_ICON_ACTIVE,
                        inactive: AssetsConstant.OFFICEBUILDING_ICON_INACTIVE
                    },
                    url: !isVivliAdmin && !isOrgOrVivliAdmin ? `orgs/${orgId}` : 'orgs',
                    defaultTab: '',
                    managementTab: true
                });
                links.splice(1, 0, {
                    title: 'Users',
                    icon: {
                        active: AssetsConstant.ORGANIZATIONS_ICON_ACTIVE,
                        inactive: AssetsConstant.ORGANIZATIONS_ICON_INACTIVE
                    },
                    url: `users`,
                    defaultTab: '',
                    managementTab: true
                });
            }
        }

        if (isOpsAdmin) {
            links.splice(links.length - 1, 0, {
                title: 'Admin Console',
                icon: {
                    active: AssetsConstant.ORGANIZATIONS_ICON_ACTIVE,
                    inactive: AssetsConstant.ORGANIZATIONS_ICON_INACTIVE
                },
                url: `adminConsole`,
                defaultTab: 'templateStudy',
                managementTab: true
            });
        }

        //remove any links that are empty placeholders or that the user doesn't have permission to see (in case of any misconfigurations)
        //TODO - check for portal access + why they are there
        const filteredLinks = links.filter((link) => {
            if (!link.title) return false; // remove empty placeholders
            return true;
        });

        filteredLinks.sort((a, b) => {
            if (!a.title) return 1;
            if (!b.title) return -1;

            const indexA = order.indexOf(a.title);
            const indexB = order.indexOf(b.title);
            return indexA - indexB;
        });

        return filteredLinks;
    };

    return {
        getUserRoutes
    };
}
